import { z } from 'zod';
import {
  DateFilter,
  DateTimeSchema,
  ObjectIdSchema,
  ObjectIdString,
  OperationExportFormats,
} from '../common.js';
import { OperationStatus } from './operations/operationSchema.js';

export const FileExportStatus = z.enum(['pending', 'processing', 'completed', 'failed', 'expired']);
export type FileExportStatus = z.infer<typeof FileExportStatus>;

export const FileExportType = z.enum(['operations', 'opportunities', 'bullhornUsers']);
export type FileExportType = z.infer<typeof FileExportType>;

export const FileExportFormat = z.enum(['csv', 'xlsx', 'pdf']);
export type FileExportFormat = z.infer<typeof FileExportFormat>;

const baseExportParams = z.looseObject({
  type: FileExportType,
  format: FileExportFormat,
});
export const FileExportParams = z.discriminatedUnion('type', [
  z.object({
    ...baseExportParams.shape,
    type: z.literal('operation'),
    dateFilter: DateFilter.nullish(),
    status: OperationStatus.array().nullish(),
    candidateId: z.union([ObjectIdString, z.string()]).nullish(),
    parentOperationId: ObjectIdSchema.nullish(),
    accountId: ObjectIdSchema,
  }),
  z.object({
    type: z.literal('bullhornUsers'),
    format: z.literal('csv'),
    integrationId: ObjectIdSchema,
    accountId: ObjectIdSchema,
  }),
  z.object({
    ...baseExportParams.shape,
    type: z.literal('opportunities'),
    format: FileExportFormat,
    accountId: ObjectIdSchema,
  }),
]);
export type FileExportParams = z.infer<typeof FileExportParams>;

export const IFileExport = z.object({
  type: FileExportType,
  format: FileExportFormat,
  status: FileExportStatus,

  parameters: FileExportParams,

  // Result data
  content: z.string().optional(),
  contentType: z.string().optional(),
  filename: z.string().optional(),
  headers: z.record(z.string(), z.string()).optional(),

  // Error tracking
  error: z.string().optional(),
  errorDetails: z.unknown().optional(),

  // Relationships
  account: ObjectIdSchema,
  user: ObjectIdSchema,
  integration: ObjectIdSchema.optional(),

  // Timestamps
  expiresAt: DateTimeSchema,
  completedAt: DateTimeSchema.optional(),
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema,
});
export type IFileExport = z.infer<typeof IFileExport>;

export const FileDownloadDTO = IFileExport.pick({
  format: true,
  content: true,
  contentType: true,
  filename: true,
  headers: true,
})
  .required()
  .safeExtend({ id: ObjectIdString })
  .describe("DTO for a file's content to send to the frontend for download");
export type FileDownloadDTO = z.infer<typeof FileDownloadDTO>;
